/**
 * RoundNotificationStore.ts
 * Zustand store for managing round notifications and escalation rules
 * Feature 4.7 - Alerts, Notifications & Escalations
 *
 * Refactored from MobX to Zustand for consistency with other stores
 */
import { create } from "zustand";
import axiosInstance from "../common/axiosInstance";

// Types
export interface NotificationRecipient {
  userId: string;
  role: string;
  userName?: string;
  email?: string;
  status: "pending" | "sent" | "delivered" | "read" | "acknowledged" | "failed";
  sentAt?: string;
  readAt?: string;
  acknowledgedAt?: string;
}

export interface RoundNotification {
  _id: string;
  /** Backend uses notificationNumber; we accept both for compatibility */
  notificationCode?: string;
  notificationNumber?: string;
  eventType:
    | "critical_issue"
    | "missed_round"
    | "sla_breach"
    | "repeat_noncompliance"
    | "escalation";
  priority: "critical" | "high" | "medium" | "low";
  title: string;
  message: string;
  channels: string[];
  recipients: NotificationRecipient[];
  status:
    | "pending"
    | "sent"
    | "partially_delivered"
    | "delivered"
    | "read"
    | "acknowledged"
    | "failed";
  issueId?: string;
  roundExecutionId?: string;
  escalationLevel: number;
  deliveryStats: {
    total: number;
    sent: number;
    delivered: number;
    read: number;
    acknowledged: number;
    failed: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface EscalationLevel {
  level: number;
  roleName: string;
  roleId?: string;
  timeMinutes: number;
  channels: string[];
}

export interface EscalationRule {
  _id: string;
  ruleCode: string;
  name: string;
  description?: string;
  roundType: "Safety" | "Facility" | "Fire" | "Infection Control" | "All";
  triggerEvent:
    | "missed_round"
    | "sla_breach"
    | "critical_issue"
    | "repeat_noncompliance";
  escalationLevels: EscalationLevel[];
  autoEscalationEnabled: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationStats {
  byEventType: Record<string, number>;
  byPriority: Record<string, number>;
  unreadCount: number;
  recentCount: number;
}

export interface EscalationDefaults {
  defaultLevels: EscalationLevel[];
  triggerEvents: string[];
  roundTypes: string[];
  channels: string[];
}

// Store state interface
interface RoundNotificationState {
  // State
  notifications: RoundNotification[];
  myNotifications: RoundNotification[];
  escalationRules: EscalationRule[];
  currentNotification: RoundNotification | null;
  currentRule: EscalationRule | null;
  stats: NotificationStats | null;
  defaults: EscalationDefaults | null;
  loading: boolean;
  rulesLoading: boolean;
  error: string | null;
  unreadCount: number;
  totalPages: number;
  currentPage: number;

  // Notification Actions
  fetchNotifications: (
    page?: number,
    filters?: Record<string, string>
  ) => Promise<void>;
  fetchMyNotifications: (unread?: boolean) => Promise<void>;
  fetchNotificationById: (id: string) => Promise<void>;
  fetchStats: () => Promise<void>;
  markAsRead: (id: string) => Promise<boolean>;
  markAllAsRead: () => Promise<boolean>;
  acknowledgeNotification: (id: string) => Promise<boolean>;
  manualEscalate: (issueId: string) => Promise<boolean>;
  clearCurrentNotification: () => void;

  // Escalation Rule Actions
  fetchEscalationRules: (filters?: Record<string, string>) => Promise<void>;
  fetchDefaults: () => Promise<void>;
  createEscalationRule: (
    data: Partial<EscalationRule>
  ) => Promise<EscalationRule | null>;
  updateEscalationRule: (
    id: string,
    data: Partial<EscalationRule>
  ) => Promise<EscalationRule | null>;
  deleteEscalationRule: (id: string) => Promise<boolean>;

  // Utilities
  clearError: () => void;
}

export const useRoundNotificationStore = create<RoundNotificationState>(
  (set, get) => ({
    // Initial state
    notifications: [],
    myNotifications: [],
    escalationRules: [],
    currentNotification: null,
    currentRule: null,
    stats: null,
    defaults: null,
    loading: false,
    rulesLoading: false,
    error: null,
    unreadCount: 0,
    totalPages: 1,
    currentPage: 1,

    // ===== NOTIFICATIONS =====

    fetchNotifications: async (page = 1, filters = {}) => {
      set({ loading: true, error: null });
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: "20",
          ...filters,
        });
        const response = await axiosInstance.get(
          `/api/round-notifications?${params}`
        );

        if (response.data.success) {
          set({
            notifications: response.data.data,
            totalPages: response.data.totalPages || 1,
            currentPage: response.data.currentPage || 1,
            loading: false,
          });
        } else {
          throw new Error(
            response.data.error || "Failed to fetch notifications"
          );
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to fetch notifications";
        set({ error: errorMessage, loading: false });
      }
    },

    fetchMyNotifications: async (unread = false) => {
      set({ loading: true, error: null });
      try {
        const params = unread ? "?unread=true" : "";
        const response = await axiosInstance.get(
          `/api/round-notifications/my${params}`
        );

        if (response.data.success) {
          set({
            myNotifications: response.data.data,
            unreadCount: response.data.unreadCount || 0,
            loading: false,
          });
        } else {
          throw new Error(
            response.data.error || "Failed to fetch notifications"
          );
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to fetch notifications";
        set({ error: errorMessage, loading: false });
      }
    },

    fetchNotificationById: async (id: string) => {
      set({ loading: true, error: null });
      try {
        const response = await axiosInstance.get(
          `/api/round-notifications/${id}`
        );

        if (response.data.success) {
          set({
            currentNotification: response.data.data,
            loading: false,
          });
        } else {
          throw new Error(
            response.data.error || "Failed to fetch notification"
          );
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to fetch notification";
        set({ error: errorMessage, loading: false });
      }
    },

    fetchStats: async () => {
      try {
        const response = await axiosInstance.get(
          "/api/round-notifications/stats"
        );

        if (response.data.success) {
          set({ stats: response.data.data });
        }
      } catch (error) {
        console.error("Failed to fetch notification stats:", error);
      }
    },

    markAsRead: async (id: string) => {
      try {
        await axiosInstance.put(`/api/round-notifications/${id}/read`);

        set((state) => {
          // Update in notifications list
          const notifications = state.notifications.map((n) => {
            if (n._id === id) {
              const userRecipient = n.recipients.find(
                (r) => r.status !== "read"
              );
              if (userRecipient) {
                userRecipient.status = "read";
                userRecipient.readAt = new Date().toISOString();
                if (!n.deliveryStats.read) n.deliveryStats.read = 0;
                n.deliveryStats.read++;
              }
            }
            return n;
          });

          // Update in myNotifications list
          const myNotifications = state.myNotifications.map((n) => {
            if (n._id === id) {
              const userRecipient = n.recipients.find(
                (r) => r.status !== "read"
              );
              if (userRecipient) {
                userRecipient.status = "read";
                userRecipient.readAt = new Date().toISOString();
                if (!n.deliveryStats.read) n.deliveryStats.read = 0;
                n.deliveryStats.read++;
              }
            }
            return n;
          });

          // Update currentNotification if it matches
          let currentNotification = state.currentNotification;
          if (currentNotification?._id === id) {
            const userRecipient = currentNotification.recipients.find(
              (r) => r.status !== "read"
            );
            if (userRecipient) {
              userRecipient.status = "read";
              userRecipient.readAt = new Date().toISOString();
              if (!currentNotification.deliveryStats.read)
                currentNotification.deliveryStats.read = 0;
              currentNotification.deliveryStats.read++;
            }
          }

          return {
            notifications,
            myNotifications,
            currentNotification,
            unreadCount: state.unreadCount > 0 ? state.unreadCount - 1 : 0,
          };
        });

        return true;
      } catch (error) {
        console.error("Failed to mark as read:", error);
        return false;
      }
    },

    markAllAsRead: async () => {
      try {
        const response = await axiosInstance.put(
          "/api/round-notifications/read-all"
        );

        if (response.data.success) {
          const now = new Date().toISOString();

          set((state) => {
            const updateList = (list: RoundNotification[]) =>
              list.map((n) => ({
                ...n,
                recipients: n.recipients.map((r) => ({
                  ...r,
                  status: r.readAt ? r.status : ("read" as const),
                  readAt: r.readAt || now,
                })),
                deliveryStats: {
                  ...n.deliveryStats,
                  read: n.recipients.length,
                },
              }));

            return {
              notifications: updateList(state.notifications),
              myNotifications: updateList(state.myNotifications),
              unreadCount: 0,
            };
          });
        }
        return true;
      } catch (error) {
        console.error("Failed to mark all as read:", error);
        return false;
      }
    },

    acknowledgeNotification: async (id: string) => {
      try {
        const response = await axiosInstance.put(
          `/api/round-notifications/${id}/acknowledge`
        );

        if (response.data.success) {
          set((state) => {
            const updateNotification = (n: RoundNotification) => {
              if (n._id !== id) return n;

              const updatedRecipients = n.recipients.map((r) => {
                if (r.status !== "acknowledged") {
                  return {
                    ...r,
                    status: "acknowledged" as const,
                    acknowledgedAt: new Date().toISOString(),
                  };
                }
                return r;
              });

              const allAcknowledged = updatedRecipients.every(
                (r) => r.status === "acknowledged"
              );

              return {
                ...n,
                recipients: updatedRecipients,
                status: allAcknowledged ? ("delivered" as const) : n.status,
                deliveryStats: {
                  ...n.deliveryStats,
                  acknowledged: (n.deliveryStats.acknowledged || 0) + 1,
                },
              };
            };

            return {
              notifications: state.notifications.map(updateNotification),
              myNotifications: state.myNotifications.map(updateNotification),
              currentNotification: state.currentNotification
                ? updateNotification(state.currentNotification)
                : null,
            };
          });
        }
        return true;
      } catch (error) {
        console.error("Failed to acknowledge notification:", error);
        return false;
      }
    },

    manualEscalate: async (issueId: string) => {
      try {
        const response = await axiosInstance.post(
          `/api/round-notifications/escalate/${issueId}`
        );
        return response.data.success;
      } catch (error) {
        console.error("Failed to escalate:", error);
        return false;
      }
    },

    clearCurrentNotification: () => {
      set({ currentNotification: null });
    },

    // ===== ESCALATION RULES =====

    fetchEscalationRules: async (filters = {}) => {
      set({ rulesLoading: true, error: null });
      try {
        const params = new URLSearchParams(filters);
        const response = await axiosInstance.get(
          `/api/round-escalation-rules?${params}`
        );

        if (response.data.success) {
          set({
            escalationRules: response.data.data,
            rulesLoading: false,
          });
        } else {
          throw new Error(response.data.error || "Failed to fetch rules");
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to fetch rules";
        set({ error: errorMessage, rulesLoading: false });
      }
    },

    fetchDefaults: async () => {
      try {
        const response = await axiosInstance.get(
          "/api/round-escalation-rules/defaults"
        );

        if (response.data.success) {
          set({ defaults: response.data.data });
        }
      } catch (error) {
        console.error("Failed to fetch defaults:", error);
      }
    },

    createEscalationRule: async (data: Partial<EscalationRule>) => {
      try {
        const response = await axiosInstance.post(
          "/api/round-escalation-rules",
          data
        );

        if (response.data.success) {
          set((state) => ({
            escalationRules: [response.data.data, ...state.escalationRules],
          }));
          return response.data.data;
        }
        return null;
      } catch (error) {
        console.error("Failed to create rule:", error);
        return null;
      }
    },

    updateEscalationRule: async (id: string, data: Partial<EscalationRule>) => {
      try {
        const response = await axiosInstance.put(
          `/api/round-escalation-rules/${id}`,
          data
        );

        if (response.data.success) {
          set((state) => ({
            escalationRules: state.escalationRules.map((r) =>
              r._id === id ? response.data.data : r
            ),
          }));
          return response.data.data;
        }
        return null;
      } catch (error) {
        console.error("Failed to update rule:", error);
        return null;
      }
    },

    deleteEscalationRule: async (id: string) => {
      try {
        const response = await axiosInstance.delete(
          `/api/round-escalation-rules/${id}`
        );

        if (response.data.success) {
          set((state) => ({
            escalationRules: state.escalationRules.filter((r) => r._id !== id),
          }));
          return true;
        }
        return false;
      } catch (error) {
        console.error("Failed to delete rule:", error);
        return false;
      }
    },

    // ===== UTILITIES =====

    clearError: () => {
      set({ error: null });
    },
  })
);
